import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";
import dayjs from "dayjs";

import { BucketNumberEnum } from "features/trading/buckets";

interface BucketInceptionDateModalProps {
  open: boolean;
  number: BucketNumberEnum;
  // The bucket's stored date as `YYYY-MM-DD`, or null when nobody has set
  // one — which is also what makes Clear meaningful only in the first case.
  inceptionDate: string | null;
  saving: boolean;
  // "create" is the prompt shown BEFORE the bucket exists: it seeds today
  // rather than a stored value, confirms with "Create bucket", and offers
  // no Clear (there is nothing stored to clear). Default "edit" is the
  // header's re-pick.
  mode?: "edit" | "create";
  onClose: () => void;
  // `null` clears the date back to "not set" — only reachable in "edit".
  onSave: (inceptionDate: string | null) => void;
}

/**
 * Picks (or clears) one bucket's inception date. Opened from the bucket's
 * header on the client page's Buckets tab, both to set a first date and to
 * re-pick an existing one — and, in "create" mode, from either Create
 * Bucket button, where it is the prompt that gathers the date the new
 * bucket is created with.
 *
 * ONE modal for both because the thing being picked is the same, under the
 * same rule: no future date, because an inception date is when the bucket
 * came into existence.
 *
 * The picker is the same react-multi-date-picker the account-adjustment
 * modal uses, with the same `portal` + `zIndex` so the calendar draws above
 * the dialog rather than being clipped inside it.
 */
export const BucketInceptionDateModal = ({
  open,
  number,
  inceptionDate,
  saving,
  mode = "edit",
  onClose,
  onSave,
}: BucketInceptionDateModalProps) => {
  const isCreate = mode === "create";
  // Creating defaults to today — the operator's "default being the current
  // date". Editing seeds the stored value, or nothing when unset.
  const [picked, setPicked] = useState<string>(() =>
    isCreate ? dayjs().format("YYYY-MM-DD") : (inceptionDate ?? ""),
  );

  // Re-seed every time the modal opens: a cancelled edit must not leak into
  // the next one, and the bucket may have changed underneath while the
  // modal was closed. Creating re-reads today rather than the mount's date,
  // so a tab left open overnight still defaults to the right day.
  useEffect(() => {
    if (!open) return;
    setPicked(isCreate ? dayjs().format("YYYY-MM-DD") : (inceptionDate ?? ""));
  }, [open, inceptionDate, isCreate]);

  // An inception date is when the bucket came into existence, so the future
  // is out of range here as well as rejected by the backend.
  const today = new DateObject();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {isCreate
          ? `Create Bucket ${number}`
          : `Bucket ${number} inception date`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" marginBottom={1.5}>
          {isCreate ? (
            <>
              The date this bucket came into existence. It defaults to today
              — change it if the bucket started earlier. You can re-pick or
              clear it later from the bucket&apos;s header.
            </>
          ) : (
            <>
              The date this bucket came into existence. Leave it unset if you
              don&apos;t know it yet.
            </>
          )}
        </Typography>
        {/* react-multi-date-picker wraps its input in an inline-block
            container of its own, so the input's width: 100% measures
            against that rather than the dialog — the container is what has
            to be stretched for the field to fill the row. */}
        <Box sx={{ "& .rmdp-container": { width: "100%" } }}>
          <CustomDatePicker
            format="MM/DD/YYYY"
            portal
            zIndex={1600}
            calendarPosition="bottom-left"
            maxDate={today}
            value={
              picked
                ? new DateObject({ date: picked, format: "YYYY-MM-DD" })
                : undefined
            }
            onChange={(date: DateObject | DateObject[] | null) => {
              if (!date || Array.isArray(date)) {
                setPicked("");
                return;
              }
              setPicked(date.format("YYYY-MM-DD"));
            }}
            style={{ height: "40px", width: "100%", fontSize: "16px" }}
            placeholder="Pick date"
            showOtherDays
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {/* Clear is the only way back to "not set", so it is offered only
            when there is a stored date to clear — never while creating. */}
        {!isCreate && inceptionDate && (
          <>
            <Button
              color="error"
              disabled={saving}
              onClick={() => onSave(null)}
            >
              Clear
            </Button>
            <Box flexGrow={1} />
          </>
        )}
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          // Creating always has a date to submit (it seeds today), and the
          // "unchanged" guard belongs to the edit case only — while
          // creating there is nothing stored for it to match.
          disabled={
            saving || !picked || (!isCreate && picked === inceptionDate)
          }
          startIcon={saving ? <CircularProgress size={16} /> : null}
          onClick={() => onSave(picked)}
        >
          {isCreate ? "Create bucket" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
